import React, { useState } from "react";
import { Link } from "react-router-dom";
import { getAudienceShowDateLabel, getShowDetailsPath, participatingShows } from "../../data/gigs";
import { getHeaderAuthCta, getPassportCta, useAuthSession } from "../auth/useAuthSession";
import "./gigathlon.css";

const GigathlonWordmark = () => (
  <span className="gigathlon-wordmark" aria-label="Gigathlon">
    G<span className="gigathlon-wordmark__mic" aria-hidden="true">🎤</span>g<span>athlon</span>
  </span>
);

const audienceSteps = [
  ["Join free", "Create your audience passport with a verified mobile number."],
  ["Go to a show", "Choose a participating independent comedy night in Valencia."],
  ["Check in at the venue", "Show your passport QR to the organizer to collect your digital stamp."],
  ["Discover what is next", "Use your passport to find another participating show."],
  ["Unlock rewards", "Build your stamp collection and receive pilot rewards."],
];

const GigathlonHome = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { user } = useAuthSession();
  const headerCta = getHeaderAuthCta(user);
  const passportCta = getPassportCta(user);
  const featuredShows = participatingShows
    .filter((show) => new Date(show.startAt).getTime() >= Date.now())
    .slice(0, 2);

  return (
    <div className="gigathlon-page">
      <header className={`gigathlon-header ${isMobileMenuOpen ? "gigathlon-header--menu-open" : ""}`}>
        <Link className="gigathlon-header__brand" to="/" aria-label="Gigathlon home"><GigathlonWordmark /></Link>
        <nav className="gigathlon-header__nav" aria-label="Main navigation">
          <a href="#how-it-works">How it works</a>
          <Link to="/valencia">Shows</Link>
          <Link to="/passport">My Passport</Link>
        </nav>
        <div className="gigathlon-header__actions">
          <Link className="gigathlon-header__signin" to={headerCta.to}>{headerCta.label}</Link>
          <button className="gigathlon-header__menu-button" type="button" aria-expanded={isMobileMenuOpen} aria-controls="gigathlon-mobile-menu" aria-label="Open navigation menu" onClick={() => setIsMobileMenuOpen((isOpen) => !isOpen)}>
            <span /><span /><span />
          </button>
        </div>
        <nav className="gigathlon-mobile-menu" id="gigathlon-mobile-menu" aria-label="Mobile navigation">
          <a href="#how-it-works" onClick={() => setIsMobileMenuOpen(false)}>How it works</a>
          <Link to="/valencia" onClick={() => setIsMobileMenuOpen(false)}>Shows</Link>
          <Link to="/passport" onClick={() => setIsMobileMenuOpen(false)}>My Passport</Link>
        </nav>
      </header>

      <main>
        <section className="gigathlon-hero" aria-labelledby="gigathlon-title">
          <div className="gigathlon-container">
            <div className="gigathlon-hero__copy">
              <p className="gigathlon-hero__eyebrow">Valencia Comedy Passport · Early Access</p>
              <h1 id="gigathlon-title">Go to more comedy. <span>Get rewarded.</span></h1>
              <p className="gigathlon-hero__tagline">Discover participating Valencia comedy shows, attend in person, and collect a digital stamp at the venue.</p>
              <div className="gigathlon-hero__actions">
                <Link className="courses-start-button" to={passportCta.to}>{passportCta.label}</Link>
                <Link className="gigathlon-secondary-link" to="/valencia">Explore Valencia shows</Link>
              </div>
              <p className="gigathlon-hero__pilot-note">Free to join. No changes to how you normally book show tickets.</p>
            </div>
          </div>
        </section>

        <section className="gigathlon-section gigathlon-how" id="how-it-works">
          <div className="gigathlon-container">
            <div className="gigathlon-section__header gigathlon-section__header--center">
              <span>How it works</span>
              <h2>Your night out becomes your next discovery.</h2>
              <p>The passport sits across participating shows. Each venue keeps its own ticketing; Gigathlon tracks your audience journey.</p>
            </div>
            <div className="gigathlon-steps gigathlon-steps--audience">
              {audienceSteps.map(([title, copy], index) => (
                <article className="gigathlon-step" key={title}>
                  <span>{index + 1}</span><h3>{title}</h3><p>{copy}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="passport-results" aria-labelledby="featured-shows-title">
          <div className="gigathlon-container">
            <div className="passport-results__heading">
              <span>Participating in Valencia</span>
              <h2 id="featured-shows-title">Find your next comedy night.</h2>
            </div>
            <div className="audience-show-preview">
              {featuredShows.map((show) => (
                <article className="step gig-card" key={show.id}>
                  <img className="gig-card__image" src={show.image} srcSet={show.imageSrcSet || undefined} sizes="(max-width: 680px) 92vw, 420px" alt={`${show.title} comedy show`} />
                  <div className="gig-card__body">
                    <p className="gig-card__meta">{show.language} · {getAudienceShowDateLabel(show)}</p>
                    <h3>{show.title}</h3><p>{show.description}</p>
                  </div>
                  <Link className="gig-card__cta" to={getShowDetailsPath(show)} aria-label={`View ${show.title}`}>
                    View show
                  </Link>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="gigathlon-audience-promise" aria-labelledby="audience-promise-title">
          <div className="gigathlon-container">
            <p>Powered by Gigathlon</p>
            <h2 id="audience-promise-title">One passport for independent comedy across Valencia.</h2>
            <p>Early Access is testing one thing: whether a great show can lead you to another great room in the city.</p>
          </div>
        </section>
      </main>

      <footer className="gigathlon-footer">
        <div className="gigathlon-container gigathlon-footer__layout">
          <div><GigathlonWordmark /><p>Go to more comedy. Collect stamps. Get rewarded.</p></div>
          <div><h3>Passport</h3><Link to="/valencia">Valencia shows</Link><Link to="/passport">My Passport</Link></div>
          <div><h3>Support</h3><a href="https://wa.me/447739343143?text=Hi%2C%20I%20would%20need%20support%20regarding%20the%20Valencia%20Comedy%20Passport" target="_blank" rel="noreferrer">WhatsApp support</a></div>
        </div>
      </footer>
    </div>
  );
};

export default GigathlonHome;
